import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PencilSimple, Info, Users, CurrencyCircleDollar, TrendUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import { usePermissions } from '../hooks/usePermissions';

/* ─── Centralized salary estimation (Chile) ─── */
const PREVISIONAL_RATE = 0.2015; // AFP ~12.5% + Salud ~7% + Seguro Cesantia ~0.6% + SIS ~1.5%

function calculateEstimatedNetSalary(grossSalary) {
  if (!grossSalary || grossSalary <= 0) return 0;
  const deductions = grossSalary * PREVISIONAL_RATE;
  return Math.round(grossSalary - deductions);
}

/* ─── Resolve effective gross salary (prepared for future custom salary) ─── */
function getEmployeeGrossSalary(worker, cargoMap) {
  if (worker.custom_gross_salary != null && worker.custom_gross_salary > 0) {
    return worker.custom_gross_salary;
  }
  const cargo = cargoMap[worker.cargo];
  return cargo?.sueldo_bruto || 0;
}

/* ─── Format CLP ─── */
const formatCLP = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value);
};

/* ─── KPI Card ─── */
const KpiCard = ({ icon: Icon, title, value, prefix = '', testId }) => (
  <div className="bg-white rounded-xl p-5 flex-1 min-w-0" style={{ border: '1px solid var(--border-soft)' }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--people-accent-light)' }}>
        <Icon size={16} style={{ color: 'var(--people-primary)' }} />
      </div>
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{title}</span>
    </div>
    <p className="font-mono text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
      {prefix}{value}
    </p>
  </div>
);

/* ─── Tooltip ─── */
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="inline-flex"
      >
        <Info size={14} style={{ color: 'var(--text-muted)' }} />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-white rounded-lg text-xs leading-relaxed shadow-lg" style={{ border: '1px solid var(--border-soft)', color: 'var(--text-secondary)' }}>
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 -mt-1" style={{ borderRight: '1px solid var(--border-soft)', borderBottom: '1px solid var(--border-soft)' }}></div>
        </div>
      )}
    </span>
  );
};

/* ─── Main Component ─── */
const RemuneracionesPage = () => {
  const { axiosInstance, isAdmin } = useAuth();
  const { canManage } = usePermissions();
  const canEdit = canManage('remuneraciones');
  const [data, setData] = useState({ cargos: [], totals: { costo_bruto_mensual: 0, total_trabajadores_activos: 0 } });
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [editData, setEditData] = useState({ sueldo_bruto: 0, anticipo_diario: 0 });
  const [saving, setSaving] = useState(false);
  const [activeTab] = useState('cargo');

  const loadData = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/remuneraciones/summary');
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [axiosInstance]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalLiquidoEstimado = data.cargos.reduce((sum, c) => {
    return sum + calculateEstimatedNetSalary(c.sueldo_bruto || 0) * (c.worker_count || 0);
  }, 0);

  const openEdit = (cargo) => {
    setEditModal(cargo);
    setEditData({ sueldo_bruto: cargo.sueldo_bruto || 0, anticipo_diario: cargo.anticipo_diario || 0 });
  };

  const saveEdit = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/cargos/${editModal.id}`, {
        sueldo_bruto: editData.sueldo_bruto,
        anticipo_diario: editData.anticipo_diario,
      });
      toast.success('Remuneracion actualizada');
      setEditModal(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const editNetEstimate = calculateEstimatedNetSalary(editData.sueldo_bruto);

  return (
    <div className="space-y-6" data-testid="remuneraciones-page">
      {/* Header */}
      <div>
        <h1 className="page-title text-2xl">Remuneraciones</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
          Configura la remuneracion base de cada cargo y obten una estimacion del costo mensual de tu dotacion.
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          Los valores definidos por cargo se utilizaran como remuneracion predeterminada para nuevos trabajadores y podran personalizarse individualmente mas adelante.
        </p>
      </div>

      {/* Tabs (prepared for future "Por trabajador") */}
      <div className="flex gap-1" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <button
          className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px ${activeTab === 'cargo' ? '' : ''}`}
          style={activeTab === 'cargo' ? { color: 'var(--people-primary)', borderBottom: '2px solid var(--people-primary)' } : { color: 'var(--text-muted)', borderBottom: '2px solid transparent' }}
          data-testid="tab-por-cargo"
        >
          Por cargo
        </button>
        <button
          className="px-4 py-2 text-sm font-medium -mb-px cursor-not-allowed opacity-40"
          style={{ color: 'var(--text-muted)', borderBottom: '2px solid transparent' }}
          disabled
          title="Disponible proximamente"
          data-testid="tab-por-trabajador"
        >
          Por trabajador
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={CurrencyCircleDollar}
          title="Costo bruto mensual"
          value={formatCLP(data.totals.costo_bruto_mensual)}
          testId="kpi-costo-bruto"
        />
        <KpiCard
          icon={TrendUp}
          title="Liquido estimado"
          prefix="≈ "
          value={formatCLP(totalLiquidoEstimado)}
          testId="kpi-liquido-estimado"
        />
        <KpiCard
          icon={Users}
          title="Trabajadores activos"
          value={data.totals.total_trabajadores_activos}
          testId="kpi-trabajadores"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-soft)' }}>
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
        ) : data.cargos.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            No hay cargos configurados. Crea cargos desde la seccion de Configuracion.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--people-accent-subtle)', borderBottom: '1px solid var(--border-soft)' }}>
                  <th className="text-left px-5 py-3.5 text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--text-secondary)' }}>Cargo</th>
                  <th className="text-right px-5 py-3.5 text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--text-secondary)' }}>Sueldo Bruto</th>
                  <th className="text-right px-5 py-3.5 text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--text-secondary)' }}>
                    Liquido Estimado
                    <InfoTooltip text="Estimacion referencial segun descuentos previsionales y tributarios. El monto real puede variar segun AFP, salud, tipo de contrato y situacion particular del trabajador." />
                  </th>
                  <th className="text-center px-5 py-3.5 text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--text-secondary)' }}>Trabajadores</th>
                  <th className="text-right px-5 py-3.5 text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--text-secondary)' }}>Costo Bruto Mensual</th>
                  {canEdit && (
                    <th className="text-center px-5 py-3.5 text-xs uppercase tracking-wider font-semibold w-20" style={{ color: 'var(--text-secondary)' }}></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.cargos.map(cargo => {
                  const netEstimate = calculateEstimatedNetSalary(cargo.sueldo_bruto || 0);
                  const hasBruto = (cargo.sueldo_bruto || 0) > 0;

                  return (
                    <tr key={cargo.id} className="transition-colors" style={{ borderBottom: '1px solid var(--border-muted)' }} data-testid={`cargo-row-${cargo.id}`}>
                      <td className="px-5 py-3.5">
                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{cargo.name}</span>
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono">
                        {hasBruto ? (
                          <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formatCLP(cargo.sueldo_bruto)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>Sin configurar</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono">
                        {hasBruto ? (
                          <span style={{ color: 'var(--text-secondary)' }}>≈ {formatCLP(netEstimate)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {cargo.worker_count > 0 ? (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold" style={{ background: 'var(--people-accent-light)', color: 'var(--people-primary)' }}>
                            {cargo.worker_count}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>0</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono">
                        {cargo.costo_bruto_mensual > 0 ? (
                          <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formatCLP(cargo.costo_bruto_mensual)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => openEdit(cargo)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: 'var(--text-muted)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--people-primary)'; e.currentTarget.style.background = 'var(--people-accent-subtle)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
                            title="Editar remuneracion"
                            data-testid={`edit-cargo-${cargo.id}`}
                          >
                            <PencilSimple size={17} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" style={{ border: '1px solid var(--border-soft)' }}>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Editar remuneracion</DialogTitle>
            <DialogDescription className="sr-only">Configurar sueldo bruto y anticipo diario del cargo</DialogDescription>
            {editModal && (
              <p className="text-sm font-semibold mt-1" style={{ color: 'var(--people-primary)' }}>{editModal.name}</p>
            )}
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Sueldo Bruto */}
            <div>
              <label className="form-label">Sueldo Bruto Mensual</label>
              <input
                type="number"
                value={editData.sueldo_bruto}
                onChange={e => setEditData({ ...editData, sueldo_bruto: parseFloat(e.target.value) || 0 })}
                className="form-input font-mono text-base"
                min="0"
                step="10000"
                data-testid="edit-sueldo-bruto"
              />
            </div>

            {/* Liquido Estimado (read-only, calculated) */}
            <div className="rounded-lg p-4" style={{ background: 'var(--people-accent-subtle)', border: '1px solid var(--border-soft)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  Liquido Estimado
                </span>
                <span className="font-mono text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  ≈ {formatCLP(editNetEstimate)}
                </span>
              </div>
              <p className="text-[11px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                Estimacion referencial. El sueldo liquido real puede variar segun las condiciones particulares del trabajador.
              </p>
            </div>

            {/* Anticipo Diario */}
            <div>
              <label className="form-label">Anticipo Diario Sugerido</label>
              <input
                type="number"
                value={editData.anticipo_diario}
                onChange={e => setEditData({ ...editData, anticipo_diario: parseFloat(e.target.value) || 0 })}
                className="form-input font-mono"
                min="0"
                step="500"
                data-testid="edit-anticipo-diario"
              />
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                Se asigna como Valor Dia al crear un nuevo trabajador con este cargo.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditModal(null)} className="rounded-lg">
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={saveEdit}
              disabled={saving}
              className="rounded-lg text-white"
              style={{ background: 'var(--people-primary)' }}
              data-testid="save-remuneracion-btn"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemuneracionesPage;
